import { PDFDocument, rgb } from 'pdf-lib';
import { downloadBlob } from './fileUtils';

/**
 * Loads a PDF from a File object
 * @param {File} file - PDF file
 * @returns {Promise<PDFDocument>}
 */
export const loadPDF = async (file) => {
  const arrayBuffer = await file.arrayBuffer();
  return await PDFDocument.load(arrayBuffer);
};

/**
 * Loads a PDF from ArrayBuffer
 * @param {ArrayBuffer} arrayBuffer - PDF data
 * @returns {Promise<PDFDocument>}
 */
export const loadPDFFromBuffer = async (arrayBuffer) => {
  return await PDFDocument.load(arrayBuffer);
};

/**
 * Splits a PDF into multiple PDFs based on page ranges
 * @param {PDFDocument} pdfDoc - Source PDF document
 * @param {Array<Array<number>>} ranges - Array of [start, end] page indices (0-based)
 * @returns {Promise<Array<PDFDocument>>}
 */
export const splitPDF = async (pdfDoc, ranges) => {
  const splitDocs = [];

  for (const [start, end] of ranges) {
    const newDoc = await PDFDocument.create();
    const pages = await newDoc.copyPages(pdfDoc, Array.from(
      { length: end - start + 1 },
      (_, i) => start + i
    ));
    pages.forEach(page => newDoc.addPage(page));
    splitDocs.push(newDoc);
  }

  return splitDocs;
};

/**
 * Extracts specific pages from a PDF
 * @param {PDFDocument} pdfDoc - Source PDF document
 * @param {Array<number>} pageIndices - Array of page indices to extract (0-based)
 * @returns {Promise<PDFDocument>}
 */
export const extractPages = async (pdfDoc, pageIndices) => {
  const newDoc = await PDFDocument.create();
  const pages = await newDoc.copyPages(pdfDoc, pageIndices);
  pages.forEach(page => newDoc.addPage(page));
  return newDoc;
};

/**
 * Splits PDF into individual pages
 * @param {PDFDocument} pdfDoc - Source PDF document
 * @returns {Promise<Array<PDFDocument>>}
 */
export const splitIntoIndividualPages = async (pdfDoc) => {
  const pageCount = pdfDoc.getPageCount();
  const individualDocs = [];

  for (let i = 0; i < pageCount; i++) {
    const newDoc = await PDFDocument.create();
    const [page] = await newDoc.copyPages(pdfDoc, [i]);
    newDoc.addPage(page);
    individualDocs.push(newDoc);
  }

  return individualDocs;
};

/**
 * Adds a signature image to a PDF at specified position
 * @param {PDFDocument} pdfDoc - PDF document
 * @param {string} signatureDataUrl - Signature image as data URL
 * @param {number} pageIndex - Page index (0-based)
 * @param {Object} position - {x, y, width, height} in canvas coordinates (origin top-left)
 * @returns {Promise<PDFDocument>}
 */
export const addSignature = async (pdfDoc, signatureDataUrl, pageIndex, position) => {
  const page = pdfDoc.getPage(pageIndex);
  const { height: pageHeight } = page.getSize();

  // Convert data URL to bytes
  const imageBytes = await fetch(signatureDataUrl).then(res => res.arrayBuffer());

  // Embed the image
  let image;
  if (signatureDataUrl.startsWith('data:image/png')) {
    image = await pdfDoc.embedPng(imageBytes);
  } else if (signatureDataUrl.startsWith('data:image/jpeg') || signatureDataUrl.startsWith('data:image/jpg')) {
    image = await pdfDoc.embedJpg(imageBytes);
  } else {
    throw new Error('Unsupported image format. Use PNG or JPEG.');
  }

  // Convert canvas coordinates (origin top-left, Y down) to PDF coordinates (origin bottom-left, Y up)
  const pdfY = pageHeight - position.y - position.height;

  // Draw the signature on the page
  page.drawImage(image, {
    x: position.x,
    y: pdfY,
    width: position.width,
    height: position.height,
  });

  return pdfDoc;
};

/**
 * Fills form fields in a PDF
 * @param {PDFDocument} pdfDoc - PDF document with form
 * @param {Object} formData - Key-value pairs of field names and values
 * @returns {Promise<PDFDocument>}
 */
export const fillForm = async (pdfDoc, formData) => {
  const form = pdfDoc.getForm();

  Object.entries(formData).forEach(([fieldName, value]) => {
    try {
      const field = form.getField(fieldName);
      const fieldType = field.constructor.name;

      if (fieldType === 'PDFTextField') {
        field.setText(String(value));
      } else if (fieldType === 'PDFCheckBox') {
        if (value) {
          field.check();
        } else {
          field.uncheck();
        }
      } else if (fieldType === 'PDFRadioGroup') {
        field.select(value);
      } else if (fieldType === 'PDFDropdown') {
        field.select(value);
      }
    } catch (error) {
      console.warn(`Could not fill field ${fieldName}:`, error);
    }
  });

  // Flatten the form to make it non-editable
  form.flatten();

  return pdfDoc;
};

/**
 * Gets form fields from a PDF
 * @param {PDFDocument} pdfDoc - PDF document
 * @returns {Array<Object>} Array of form field objects
 */
export const getFormFields = (pdfDoc) => {
  try {
    const form = pdfDoc.getForm();
    const fields = form.getFields();

    return fields.map(field => {
      const fieldType = field.constructor.name;
      const name = field.getName();

      let type = 'text';
      let options = null;
      let value = null;

      if (fieldType === 'PDFTextField') {
        type = 'text';
        value = field.getText() || '';
      } else if (fieldType === 'PDFCheckBox') {
        type = 'checkbox';
        value = field.isChecked();
      } else if (fieldType === 'PDFRadioGroup') {
        type = 'radio';
        options = field.getOptions();
        value = field.getSelected();
      } else if (fieldType === 'PDFDropdown') {
        type = 'dropdown';
        options = field.getOptions();
        value = field.getSelected();
      }

      return {
        name,
        type,
        value,
        options
      };
    });
  } catch (error) {
    console.warn('No form fields found or error reading form:', error);
    return [];
  }
};

/**
 * Downloads a PDF document
 * @param {PDFDocument} pdfDoc - PDF document to download
 * @param {string} filename - Filename for download
 */
export const downloadPDF = async (pdfDoc, filename) => {
  const pdfBytes = await pdfDoc.save();
  const blob = new Blob([pdfBytes], { type: 'application/pdf' });
  downloadBlob(blob, filename);
};

/**
 * Converts PDF document to bytes
 * @param {PDFDocument} pdfDoc - PDF document
 * @returns {Promise<Uint8Array>}
 */
export const pdfToBytes = async (pdfDoc) => {
  return await pdfDoc.save();
};

/**
 * Gets the number of pages in a PDF
 * @param {PDFDocument} pdfDoc - PDF document
 * @returns {number}
 */
export const getPageCount = (pdfDoc) => {
  return pdfDoc.getPageCount();
};

/**
 * Merges multiple PDFs into one
 * @param {Array<PDFDocument>} pdfDocs - Array of PDF documents
 * @returns {Promise<PDFDocument>}
 */
export const mergePDFs = async (pdfDocs) => {
  const mergedDoc = await PDFDocument.create();

  for (const pdfDoc of pdfDocs) {
    const pageCount = pdfDoc.getPageCount();
    const pages = await mergedDoc.copyPages(pdfDoc, Array.from({ length: pageCount }, (_, i) => i));
    pages.forEach(page => mergedDoc.addPage(page));
  }

  return mergedDoc;
};
